import React, { useState } from 'react';
import { Wallet, Bitcoin, RefreshCcw, Flame, Zap, Building2, Moon, Sun, Plug, InfoIcon } from 'lucide-react';
import { BalanceCard } from './BalanceCard';
import ErrorMessage from './ErrorMessage';
import { useBalanceData } from '../hooks/useBalanceData';
import { useTheme } from '../context/ThemeContext';

// Updated LoadingSpinner with theme support
const ThemedLoadingSpinner = () => (
  <div className="flex items-center justify-center">
    <div className="w-5 h-5 border-b-2 border-gray-900 rounded-full dark:border-white animate-spin"></div>
  </div>
);

// Add a tooltip component for the total treasury card
const Tooltip = ({ text, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="relative">
      <button 
        className="text-gray-600 transition-colors duration-500 dark:text-white/60 hover:text-gray-800 dark:hover:text-white/90"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children}
      </button>
      
      {showTooltip && (
        <div className="absolute right-0 z-10 w-64 p-3 -mt-2 text-xs text-white transform translate-y-full bg-gray-800 rounded-md shadow-lg">
          {text}
        </div>
      )}
    </div>
  );
};

const SpaceTreasuryDashboard = () => {
  const { darkMode, toggleDarkMode } = useTheme();

  // Add this state and effect for the animation
  const [isRotating, setIsRotating] = useState(false);

  const handleThemeToggle = () => {
    setIsRotating(true);
    toggleDarkMode();
    setTimeout(() => setIsRotating(false), 500);
  };

  const {
    balances,
    bitcoinPrice,
    isLoading,
    isAnyLoading,
    lastUpdated,
    mostRecentUpdate,
    errors,
    refreshAll,
    refreshSection
  } = useBalanceData();

  // Calculate total treasury value
  const totalValue = Object.values(balances).reduce((total, balance) => {
    // For Mercury, use usdBalance directly
    if ('usdBalance' in balance) return total + (balance.usdBalance || 0);
    // For other services, use valueUSD
    return total + (balance.valueUSD || 0);
  }, 0);

  // Add this function to determine status color
  const getStatusColor = (service) => {
    const serviceLastUpdated = lastUpdated[service];
    if (!serviceLastUpdated) return 'bg-gray-500'; // No data yet
    
    const isRecent = Date.now() - serviceLastUpdated < 5 * 60 * 1000;
    
    // Check if service has errors
    if (errors[service]) {
      return 'bg-red-500';
    }
    
    // Check if service is currently loading
    if (isLoading[service]) {
      return 'bg-blue-500'; // Loading state
    }
    
    // Check if balance is zero (might indicate an issue)
    const hasZeroBalance = 
      (service === 'onChain' && balances.onChain.balance === 0) ||
      (service === 'mercury' && balances.mercury.usdBalance === 0) ||
      (service === 'strike' && balances.strike.btcBalance === 0 && balances.strike.usdBalance === 0) ||
      (service === 'lightning' && balances.lightning.balance === 0);
      
    if (hasZeroBalance && !isLoading[service]) {
      return 'bg-yellow-500'; // Warning
    }
    
    return isRecent ? 'bg-green-500' : 'bg-yellow-500';
  };

  const DebugInfo = ({ data, isVisible }) => {
    if (!isVisible) return null;

    return (
      <div className="p-4 mt-8 overflow-auto font-mono text-xs bg-gray-800 rounded-lg max-h-96">
        <h3 className="mb-2 text-white">Debug Information</h3>
        <pre className="text-green-400">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div className="min-h-screen text-white text-gray-900 transition-colors duration-500 bg-gray-100 dark:bg-gray-900 dark:text-white">
      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              The Space Treasury
            </h1>
            <p className="text-gray-600 dark:text-white/60">
              Current Bitcoin Price: ${bitcoinPrice?.toLocaleString() || '---'}
            </p>
            {/* Last updated indicator */}
            <div className="flex items-center h-5 gap-1 mt-1 text-xs text-gray-500 dark:text-white/40">
              {mostRecentUpdate ? (
                <>
                  <span className={`w-2 h-2 rounded-full ${Date.now() - mostRecentUpdate < 5 * 60 * 1000 ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                  <span>Last updated: {new Date(mostRecentUpdate).toLocaleString()}</span>
                </>
              ) : ''}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleThemeToggle}
              className="p-2 transition-all duration-500 bg-gray-200 rounded-full dark:bg-white/10 dark:hover:bg-white/20 hover:bg-gray-300"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              <div className={`transform transition-transform duration-500 ${isRotating ? 'rotate-180' : ''}`}>
                {darkMode ? 
                  <Sun className="w-5 h-5 text-white transition-colors duration-500" /> : 
                  <Moon className="w-5 h-5 text-gray-900 transition-colors duration-500" />
                }
              </div>
            </button>
            
            <button
              onClick={refreshAll}
              disabled={isAnyLoading}
              className="flex items-center gap-2 px-4 py-2 transition-colors bg-gray-200 rounded-lg dark:bg-white/10 dark:hover:bg-white/20 hover:bg-gray-300 disabled:opacity-50"
            >
              {isAnyLoading ? 
                <ThemedLoadingSpinner /> : 
                <RefreshCcw className="w-4 h-4 text-gray-900 transition-colors duration-500 dark:text-white" />
              }
              <span className="text-gray-900 transition-colors duration-500 dark:text-white">Refresh</span>
            </button>
          </div>
        </div>

        {errors.bitcoinPrice && (
          <div className="mb-6">
            <ErrorMessage message={`Bitcoin Price: ${errors.bitcoinPrice}`} onRetry={() => refreshSection('bitcoinPrice')} />
          </div>
        )}

        <div className="mb-8">
          <div className="p-6 transition-all duration-500 bg-white dark:bg-white/5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-white/5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 transition-colors duration-500 dark:text-white">Total Treasury Value</h3>
              <div className="flex items-center gap-2">
                <Tooltip text="Total value of The Space's Bitcoin and USD holdings across all services.">
                  <InfoIcon className="w-4 h-4" />
                </Tooltip>
                <Bitcoin className="w-6 h-6 text-orange-400" />
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900 transition-colors duration-500 dark:text-white">
              ${totalValue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <BalanceCard
            icon={<Wallet className="w-6 h-6 text-blue-400" />}
            title="On-Chain"
            balance={balances.onChain.balance}
            valueUSD={balances.onChain.valueUSD}
            isLoading={isLoading.onChain}
            error={errors.onChain}
            onRetry={() => refreshSection('onChain')}
            tooltipText="The Space's Bitcoin stored directly on the blockchain."
          />

          <BalanceCard
            icon={<Building2 className="w-6 h-6 text-green-400" />}
            title="Mercury"
            isFiat={true}
            valueUSD={balances.mercury.usdBalance}
            isLoading={isLoading.mercury}
            error={errors.mercury}
            onRetry={() => refreshSection('mercury')}
            tooltipText="The Space's USD balance in Mercury bank."
          />

          <BalanceCard
            icon={<Flame className="w-6 h-6 text-orange-400" />}
            title="Strike"
            balance={balances.strike.btcBalance}
            valueUSD={balances.strike.usdBalance}
            isLoading={isLoading.strike}
            error={errors.strike}
            onRetry={() => refreshSection('strike')}
            tooltipText="The Space's Bitcoin and USD in Strike account."
          />

          <BalanceCard
            icon={<Zap className="w-6 h-6 text-yellow-400" />}
            title="Lightning"
            balance={balances.lightning.balance}
            valueUSD={balances.lightning.valueUSD}
            isLoading={isLoading.lightning}
            error={errors.lightning}
            onRetry={() => refreshSection('lightning')}
            tooltipText="The Space's Lightning Network node balance."
            extraDetails={
              <div className="text-sm no-transition lightning-fast-transition">
                <div className="flex justify-between">
                  <span>Channels:</span>
                  <span>{balances.lightning.channelCount || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Channel Balance:</span>
                  <span>{balances.lightning.channelBalance?.toLocaleString(undefined, {
                    minimumFractionDigits: 8,
                    maximumFractionDigits: 8
                  })} BTC</span>
                </div>
                <div className="flex justify-between">
                  <span>On-chain Balance:</span>
                  <span>{balances.lightning.onChainBalance?.toLocaleString(undefined, {
                    minimumFractionDigits: 8,
                    maximumFractionDigits: 8
                  })} BTC</span>
                </div>
              </div>
            }
          />
        </div>

        <div className="p-6 mt-8 transition-all duration-500 bg-white dark:bg-white/5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-white/5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 transition-colors duration-500 dark:text-white">Service Health</h3>
            <div className="flex items-center gap-2">
              <Tooltip text="Service status: Green = OK, Yellow = Warning, Red = Error">
                <InfoIcon className="w-4 h-4" />
              </Tooltip>
              <Plug className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {Object.entries({
              'On-Chain': getStatusColor('onChain'),
              'Mercury': getStatusColor('mercury'),
              'Strike': getStatusColor('strike'),
              'Lightning': getStatusColor('lightning')
            }).map(([service, color]) => (
              <div key={service} className="flex items-center gap-2 p-2 transition-all duration-500 rounded-lg bg-white/50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10">
                <span className={`w-3 h-3 rounded-full ${color}`}></span>
                <span className="text-gray-900 transition-colors duration-500 dark:text-white">{service}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Debug information hidden for production */}
        <DebugInfo
          data={{
            balances,
            bitcoinPrice,
            lastUpdated,
            mostRecentUpdate: mostRecentUpdate ? new Date(mostRecentUpdate).toISOString() : null,
            errors,
            isLoading
          }}
          isVisible={false} // Set to false in production
        />
      </div>
    </div>
  );
};

export default SpaceTreasuryDashboard; 